<script setup>
import { ref, onMounted, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import IxcCredentialsAPI from 'dashboard/api/ixcCredentials';

const ixcUserId = ref('');
const ixcToken = ref('');
const configured = ref(false);
const lastVerifiedAt = ref(null);
const isSaving = ref(false);
const isVerifying = ref(false);
const isLoading = ref(true);

const lastVerifiedLabel = computed(() => {
  if (!lastVerifiedAt.value) return null;
  return new Date(lastVerifiedAt.value).toLocaleString();
});

const fetchCurrent = async () => {
  try {
    const { data } = await IxcCredentialsAPI.get();
    configured.value = !!data.configured;
    ixcUserId.value = data.ixc_user_id || '';
    lastVerifiedAt.value = data.last_verified_at;
    ixcToken.value = '';
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  if (!ixcUserId.value || !ixcToken.value) {
    useAlert('Preencha User ID e Token.');
    return;
  }
  isSaving.value = true;
  try {
    await IxcCredentialsAPI.save({
      agent_ixc_credential: {
        ixc_user_id: ixcUserId.value,
        ixc_token: ixcToken.value,
      },
    });
    useAlert('Credenciais IXC salvas com sucesso.');
    ixcToken.value = '';
    await fetchCurrent();
  } catch (e) {
    useAlert('Erro ao salvar credenciais.');
  } finally {
    isSaving.value = false;
  }
};

const verify = async () => {
  isVerifying.value = true;
  try {
    const { data } = await IxcCredentialsAPI.verify();
    if (data.ok) {
      useAlert('Conexão com o IXC validada.');
      lastVerifiedAt.value = data.last_verified_at;
    } else {
      useAlert(`Falha na conexão: ${data.error || 'erro desconhecido'}`);
    }
  } catch (e) {
    useAlert(`Falha na conexão: ${e?.response?.data?.error || e.message}`);
  } finally {
    isVerifying.value = false;
  }
};

const removeCredentials = async () => {
  if (!window.confirm('Remover suas credenciais IXC?')) return;
  await IxcCredentialsAPI.remove();
  configured.value = false;
  ixcUserId.value = '';
  ixcToken.value = '';
  lastVerifiedAt.value = null;
  useAlert('Credenciais removidas.');
};

onMounted(fetchCurrent);
</script>

<template>
  <div v-if="!isLoading" class="flex flex-col gap-4 max-w-2xl">
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">IXC User ID</label>
      <woot-input
        v-model="ixcUserId"
        name="ixc_user_id"
        placeholder="ex: 1"
        :styles="{ borderRadius: '12px', fontSize: '14px', marginBottom: '0' }"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        IXC Token
        <span v-if="configured" class="text-xs text-n-slate-10 font-normal">
          (deixe vazio para manter o atual)
        </span>
      </label>
      <woot-input
        v-model="ixcToken"
        name="ixc_token"
        type="password"
        :placeholder="configured ? '••••••••' : 'Cole o token gerado no IXC'"
        :styles="{ borderRadius: '12px', fontSize: '14px', marginBottom: '0' }"
      />
    </div>
    <div v-if="lastVerifiedLabel" class="text-xs text-n-slate-11">
      Última verificação: {{ lastVerifiedLabel }}
    </div>
    <div class="flex flex-row gap-2 flex-wrap">
      <NextButton
        label="Salvar"
        solid
        blue
        :disabled="isSaving"
        :is-loading="isSaving"
        @click="save"
      />
      <NextButton
        v-if="configured"
        label="Testar conexão"
        outline
        slate
        :disabled="isVerifying"
        :is-loading="isVerifying"
        @click="verify"
      />
      <NextButton
        v-if="configured"
        label="Remover"
        outline
        ruby
        @click="removeCredentials"
      />
    </div>
  </div>
</template>
